/**
 * PlatformSettingsPage.tsx — Platform infrastructure settings (admin-only)
 *
 * Only platform admins can access this. Manages:
 *   - Database connection
 *   - Redis connection
 *   - Authentication (OIDC provider)
 *   - Email provider
 *   - Storage backend
 *   - System-level webhooks (auth webhook secrets)
 *
 * Org admins CANNOT see or modify these — they only manage their own
 * Stripe connection and org-level webhooks via OrgSettingsPage.
 *
 * @since 2026-06-19 (SaaS conversion — Platform/Org settings split)
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import PlatformLayout from '../components/PlatformLayout';
import SlidePanel from '../components/SlidePanel';
import ConfirmDialog from '../components/ConfirmDialog';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Setting {
  key: string;
  value: string;
  category: string;
  is_secret: boolean;
  description: string | null;
  updated_at: string;
  has_value: boolean;
}

type CategoryStatus = 'configured' | 'partial' | 'not-configured';

interface CategoryDef {
  key: string;
  label: string;
  icon: string;
  purpose: string;
  requiredKeys: string[];
}

// Platform-only categories
const PLATFORM_CATEGORIES: CategoryDef[] = [
  { key: 'stripe', label: 'Stripe Connect', icon: '💳', purpose: 'Platform Stripe keys, webhook secrets, and public URL for Connect onboarding. Configure test + live keys and select which mode is active.', requiredKeys: ['STRIPE_MODE', 'STRIPE_TEST_SECRET_KEY', 'STRIPE_SECRET_KEY'] },
  { key: 'billing', label: 'Billing Info', icon: '🧾', purpose: 'Billing tenant ID and product configuration. The BILLING_TENANT_ID identifies which billing tenant this product maps to for payment processing.', requiredKeys: ['BILLING_TENANT_ID'] },
  { key: 'database', label: 'Database', icon: '🗄️', purpose: 'Stores all billing data — customers, subscriptions, invoices, usage events.', requiredKeys: ['BILLING_DB__HOST', 'BILLING_DB__PORT', 'BILLING_DB__NAME', 'BILLING_DB__USER', 'BILLING_DB__PASSWORD'] },
  { key: 'redis', label: 'Redis', icon: '⚡', purpose: 'Powers background jobs (invoicing, webhooks, usage aggregation) and caching.', requiredKeys: ['BILLING_REDIS__HOST', 'BILLING_REDIS__PORT'] },
  { key: 'auth', label: 'Authentication', icon: '🔐', purpose: 'Connects to Rule42 Auth for SSO, org membership, and customer provisioning.', requiredKeys: ['BILLING_AUTH__SERVICE_URL', 'BILLING_AUTH__CLIENT_ID', 'BILLING_AUTH__CLIENT_SECRET'] },
  { key: 'email', label: 'Email', icon: '📧', purpose: 'Sends invoice receipts, payment confirmations, dunning notices, and billing alerts.', requiredKeys: ['BILLING_EMAIL__PROVIDER'] },
  { key: 'storage', label: 'Storage', icon: '📁', purpose: 'Stores generated PDF invoices, credit notes, and receipt documents.', requiredKeys: ['PDF_STORAGE_BACKEND'] },
  { key: 'webhooks', label: 'System Webhooks', icon: '🔗', purpose: 'Signs outbound platform webhook payloads with HMAC-SHA256.', requiredKeys: ['BILLING_WEBHOOKS__SIGNING_SECRET'] },
  { key: 'toggles', label: 'Platform Toggles', icon: '⚙️', purpose: 'Feature flags for platform-level operations like transaction deletion and dev-mode controls.', requiredKeys: [] },
];

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

function useSettings() {
  const { token, apiBaseUrl } = useAdminAuth();
  return useQuery<Setting[]>({
    queryKey: ['platform-settings'],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/admin/settings`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      return (await res.json()).data;
    },
    enabled: !!token,
  });
}

function getCategoryStatus(cat: CategoryDef, settings: Setting[]): CategoryStatus {
  const catSettings = settings.filter((s) => s.category === cat.key);
  if (catSettings.length === 0) return 'not-configured';
  const configured = cat.requiredKeys.filter((k) => catSettings.some((s) => s.key === k && s.has_value));
  if (configured.length === cat.requiredKeys.length) return 'configured';
  if (configured.length > 0) return 'partial';
  return 'not-configured';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function PlatformSettingsPage(): React.JSX.Element {
  const { data: settings, isLoading } = useSettings();
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  return (
    <PlatformLayout title="Infrastructure Settings">
      <div className="space-y-4">
        <div className="rounded-xl border border-purple-200 bg-purple-50 p-4">
          <p className="text-sm text-purple-800">
            <strong>Platform-level settings</strong> — these control the shared infrastructure that all tenants use.
            Only platform administrators can modify these. Org admins manage their own Stripe and webhook configuration separately.
          </p>
        </div>

        {isLoading && (
          <div className="py-12 text-center text-slate-400">Loading settings...</div>
        )}

        {!isLoading && settings && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {PLATFORM_CATEGORIES.map((cat) => {
              const status = getCategoryStatus(cat, settings);
              return (
                <button
                  key={cat.key}
                  onClick={() => setActiveCategory(cat.key)}
                  className="flex items-start gap-4 rounded-xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:border-purple-300 hover:shadow-md"
                >
                  <div className="text-2xl">{cat.icon}</div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-slate-900">{cat.label}</h3>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        status === 'configured' ? 'bg-green-100 text-green-700' :
                        status === 'partial' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-slate-100 text-slate-500'
                      }`}>
                        {status === 'configured' ? 'Configured' : status === 'partial' ? 'Partial' : 'Not configured'}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">{cat.purpose}</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Category detail panel */}
        {activeCategory && (
          <SlidePanel
            open={!!activeCategory}
            onClose={() => setActiveCategory(null)}
            title={PLATFORM_CATEGORIES.find((c) => c.key === activeCategory)?.label ?? ''}
          >
            <CategoryPanel category={activeCategory} onClose={() => setActiveCategory(null)} />
          </SlidePanel>
        )}
      </div>
    </PlatformLayout>
  );
}

function CategoryPanel({ category, onClose }: { category: string; onClose: () => void }) {
  if (category === 'stripe') {
    return <StripeCategoryPanel onClose={onClose} />;
  }
  if (category === 'auth') {
    return <AuthCategoryPanel onClose={onClose} />;
  }
  if (category === 'toggles') {
    return <TogglesPanel onClose={onClose} />;
  }
  return <GenericCategoryPanel category={category} onClose={onClose} />;
}

// ---------------------------------------------------------------------------
// Stripe-specific panel with test/live sections
// ---------------------------------------------------------------------------

const STRIPE_FIELD_LABELS: Record<string, string> = {
  STRIPE_TEST_SECRET_KEY: 'Test Secret Key',
  STRIPE_TEST_PUBLISHABLE_KEY: 'Test Publishable Key',
  STRIPE_TEST_WEBHOOK_SECRET: 'Test Webhook Secret',
  STRIPE_SECRET_KEY: 'Live Secret Key',
  STRIPE_PUBLISHABLE_KEY: 'Live Publishable Key',
  STRIPE_WEBHOOK_SECRET: 'Live Webhook Secret',
  BILLING_PUBLIC_URL: 'Public URL (for Stripe Connect)',
};

const TEST_KEYS = ['STRIPE_TEST_SECRET_KEY', 'STRIPE_TEST_PUBLISHABLE_KEY', 'STRIPE_TEST_WEBHOOK_SECRET'];
const LIVE_KEYS = ['STRIPE_SECRET_KEY', 'STRIPE_PUBLISHABLE_KEY', 'STRIPE_WEBHOOK_SECRET'];

function StripeCategoryPanel({ onClose }: { onClose: () => void }) {
  const { token, apiBaseUrl } = useAdminAuth();
  const queryClient = useQueryClient();
  const { data: allSettings } = useSettings();
  // Merge both 'stripe' and 'stripe_connect' categories — they're the same
  // configuration surface. The runtime reads from both (stripe-connect-routes.ts
  // queries `category IN ('stripe', 'stripe_connect')`).
  const settings = (allSettings ?? []).filter((s) => s.category === 'stripe' || s.category === 'stripe_connect');
  const settingsMap = new Map(settings.map((s) => [s.key, s]));

  const currentMode = settingsMap.get('STRIPE_MODE')?.value || 'test';
  const [mode, setMode] = useState(currentMode);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [showEducation, setShowEducation] = useState(false);

  // Confirmation modal state for generate/rotate webhook secret
  const [confirmAction, setConfirmAction] = useState<{ key: string; action: 'generate' | 'rotate' } | null>(null);
  // Revealed secret (shown once after generate/rotate)
  const [revealedSecret, setRevealedSecret] = useState<{ key: string; value: string } | null>(null);
  // Reveal-in-progress state (eye button to view current value)
  const [revealingKey, setRevealingKey] = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: async (updates: Record<string, string>) => {
      const res = await fetch(`${apiBaseUrl}/admin/settings`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings: Object.entries(updates).map(([key, value]) => ({ key, value })) }),
      });
      if (!res.ok) throw new Error(`Save failed: ${res.status}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-settings'] });
      setEdits({});
    },
  });

  // Generate or rotate a webhook signing secret via the server endpoint
  const generateSecretMutation = useMutation({
    mutationFn: async (key: string) => {
      const res = await fetch(`${apiBaseUrl}/admin/settings/generate-webhook-secret`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ key }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error ?? `Failed: ${res.status}`);
      }
      return (await res.json()).data as { key: string; value: string; message: string };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['platform-settings'] });
      setRevealedSecret({ key: data.key, value: data.value });
      setConfirmAction(null);
    },
  });

  // Reveal the current value of a secret setting (eye button)
  const revealSecretMutation = useMutation({
    mutationFn: async (key: string) => {
      const res = await fetch(`${apiBaseUrl}/admin/settings/reveal/${key}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Reveal failed: ${res.status}`);
      return (await res.json()).data as { key: string; value: string };
    },
    onSuccess: (data) => {
      setRevealedSecret({ key: data.key, value: data.value });
      setRevealingKey(null);
    },
  });

  function handleModeChange(newMode: string) {
    setMode(newMode);
    setEdits((prev) => ({ ...prev, STRIPE_MODE: newMode }));
  }

  function renderField(key: string) {
    const s = settingsMap.get(key);
    if (!s) return null;
    const label = STRIPE_FIELD_LABELS[key] ?? key;
    const isWebhookSecret = key.includes('WEBHOOK_SECRET');
    const isRevealed = revealedSecret?.key === key;

    return (
      <div key={key} className="space-y-1">
        <div className="flex items-center justify-between">
          <label className="block text-xs font-medium text-slate-600">{label}</label>
          <div className="flex items-center gap-2">
            {s.has_value && (
              <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-700">Set</span>
            )}
            {/* Reveal current value (eye button) */}
            {s.is_secret && s.has_value && !isRevealed && (
              <button
                type="button"
                onClick={() => { setRevealingKey(key); revealSecretMutation.mutate(key); }}
                disabled={revealSecretMutation.isPending}
                className="text-xs text-slate-400 hover:text-slate-600"
                title="Reveal current value"
              >
                {revealSecretMutation.isPending && revealingKey === key ? '...' : '👁 Reveal'}
              </button>
            )}
          </div>
        </div>

        {/* If just generated/revealed, show the value in a copyable box */}
        {isRevealed ? (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={revealedSecret!.value}
                className="w-full rounded-lg border border-purple-300 bg-purple-50 px-3 py-2 font-mono text-sm text-purple-900"
              />
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard?.writeText(revealedSecret!.value);
                }}
                className="shrink-0 rounded-lg border border-slate-300 px-3 py-2 text-xs text-slate-600 hover:bg-slate-50"
              >
                Copy
              </button>
            </div>
            <p className="text-xs text-amber-600">
              ⚠️ Copy this value now — it will be masked when you leave this panel.
            </p>
            <button
              type="button"
              onClick={() => setRevealedSecret(null)}
              className="text-xs text-slate-400 hover:text-slate-600"
            >
              Hide
            </button>
          </div>
        ) : (
          <input
            type={s.is_secret ? 'password' : 'text'}
            defaultValue={s.is_secret ? '' : s.value}
            placeholder={s.is_secret ? (s.has_value ? '•••••••• (leave blank to keep)' : 'Not set') : 'Not set'}
            onChange={(e) => setEdits((prev) => ({ ...prev, [key]: e.target.value }))}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
          />
        )}

        {/* Generate / Rotate buttons for webhook secrets */}
        {isWebhookSecret && (
          <div className="flex items-center gap-2 pt-1">
            {s.has_value ? (
              <button
                type="button"
                onClick={() => setConfirmAction({ key, action: 'rotate' })}
                className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100"
              >
                🔄 Rotate Secret
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmAction({ key, action: 'generate' })}
                className="rounded-lg border border-purple-300 bg-purple-50 px-3 py-1.5 text-xs font-medium text-purple-700 hover:bg-purple-100"
              >
                ✨ Generate Secret
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      {/* Mode selector */}
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-2">Active Environment</label>
        <div className="flex rounded-lg border border-slate-200 overflow-hidden">
          <button
            onClick={() => handleModeChange('test')}
            className={`flex-1 py-2.5 text-sm font-medium transition ${
              mode === 'test'
                ? 'bg-amber-500 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            🧪 Test Mode
          </button>
          <button
            onClick={() => handleModeChange('live')}
            className={`flex-1 py-2.5 text-sm font-medium transition ${
              mode === 'live'
                ? 'bg-green-600 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            🟢 Live Mode
          </button>
        </div>
        <p className="mt-1.5 text-xs text-slate-400">
          {mode === 'test'
            ? 'Using test keys — charges go to Stripe sandbox. No real money is moved.'
            : 'Using live keys — charges are real. Ensure your keys are correct before enabling.'}
        </p>
      </div>

      {/* Test keys section */}
      <div className={`rounded-xl border p-4 space-y-3 ${
        mode === 'test' ? 'border-amber-200 bg-amber-50/50' : 'border-slate-100 bg-slate-50/50 opacity-60'
      }`}>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-700">Test Keys</span>
          {mode === 'test' && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700 uppercase">Active</span>
          )}
        </div>
        {TEST_KEYS.map(renderField)}
      </div>

      {/* Live keys section */}
      <div className={`rounded-xl border p-4 space-y-3 ${
        mode === 'live' ? 'border-green-200 bg-green-50/50' : 'border-slate-100 bg-slate-50/50 opacity-60'
      }`}>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-700">Live Keys</span>
          {mode === 'live' && (
            <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-700 uppercase">Active</span>
          )}
        </div>
        {LIVE_KEYS.map(renderField)}
      </div>

      {/* Public URL (was previously in a separate "Stripe URLs" category) */}
      <div className="rounded-xl border border-slate-200 p-4 space-y-3">
        <span className="text-sm font-semibold text-slate-700">Connect URLs</span>
        {renderField('BILLING_PUBLIC_URL')}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
          Cancel
        </button>
        <button
          onClick={() => saveMutation.mutate(edits)}
          disabled={Object.keys(edits).length === 0 || saveMutation.isPending}
          className="rounded-lg bg-purple-600 px-4 py-2 text-sm text-white hover:bg-purple-700 disabled:opacity-50"
        >
          {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* ─── Collapsible education section (bottom) ─── */}
      <div className="border-t border-slate-200 pt-4">
        <button
          type="button"
          onClick={() => setShowEducation(!showEducation)}
          className="flex w-full items-center justify-between text-sm font-medium text-slate-600 hover:text-slate-800"
        >
          <span>💡 Learn about Stripe keys & webhook secrets</span>
          <span className="text-xs">{showEducation ? '▲ Hide' : '▼ Show'}</span>
        </button>
        {showEducation && (
          <div className="mt-3 space-y-4 text-xs leading-relaxed text-slate-600">
            <div>
              <strong>Secret Key</strong> (<code className="rounded bg-slate-100 px-1">sk_test_</code> / <code className="rounded bg-slate-100 px-1">sk_live_</code>) —
              Server-side key that can create charges, manage customers, and handle subscriptions.
              <strong className="text-red-600"> Keep this private</strong> — anyone with it can charge your Stripe account.
              Get it from the <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-purple-600 underline">Stripe Dashboard → Developers → API Keys</a>.
            </div>
            <div>
              <strong>Publishable Key</strong> (<code className="rounded bg-slate-100 px-1">pk_test_</code> / <code className="rounded bg-slate-100 px-1">pk_live_</code>) —
              Client-side key used in the browser to initialize Stripe.js and render payment forms. Safe to expose publicly.
              Get it from the <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-purple-600 underline">Stripe Dashboard → Developers → API Keys</a>.
            </div>
            <div>
              <strong>Webhook Secret</strong> (<code className="rounded bg-slate-100 px-1">whsec_</code>) —
              Stripe uses this to sign every webhook event it sends to your server, so your server can verify the event
              genuinely came from Stripe and wasn't tampered with.
              <br /><br />
              <strong>Where to get it:</strong> Create a webhook endpoint in the{' '}
              <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noopener noreferrer" className="text-purple-600 underline">Stripe Dashboard → Developers → Webhooks</a>,
              pointing to <code className="rounded bg-slate-100 px-1">https://finance.rule42.ai/webhooks/stripe</code>, then copy the signing secret shown.
              <br /><br />
              <strong>Or generate one locally:</strong> Click "Generate" to create a random signing secret. This is useful for testing or
              self-managed endpoints, but for production Stripe webhooks you should use the value from the Stripe Dashboard, as Stripe needs
              to know the secret to sign events.
            </div>
            <div>
              <strong>Public URL</strong> — The public base URL of your billing service (e.g. <code className="rounded bg-slate-100 px-1">https://finance.rule42.ai</code>).
              Used by Stripe Connect to redirect users back to the billing admin after onboarding.
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <strong>Test vs Live:</strong> Test keys (<code className="rounded bg-slate-100 px-1">_test_</code>) use the Stripe sandbox —
              no real money is moved. Live keys (<code className="rounded bg-slate-100 px-1">_live_</code>) process real charges.
              Switch the active environment above to control which keys are used at runtime.
            </div>
          </div>
        )}
      </div>

      {/* Confirmation modal for generate / rotate */}
      <ConfirmDialog
        open={confirmAction !== null}
        title={confirmAction?.action === 'rotate' ? 'Rotate Webhook Signing Secret?' : 'Generate Webhook Signing Secret?'}
        message={
          confirmAction?.action === 'rotate' ? (
            <div className="space-y-2">
              <p><strong>You are about to replace the existing webhook signing secret with a new one.</strong></p>
              <p className="text-slate-600">
                <strong>What this means:</strong>
              </p>
              <ul className="ml-4 list-disc space-y-1 text-slate-600">
                <li>The old secret will stop working immediately — Stripe webhook events signed with the old secret will be rejected.</li>
                <li>You must update the signing secret in the <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noopener noreferrer" className="text-purple-600 underline">Stripe Dashboard webhook endpoint settings</a> to match, or Stripe webhooks will fail verification.</li>
                <li>Any in-flight webhook deliveries may fail during the transition.</li>
                <li>The new secret will be shown once — copy it immediately.</li>
              </ul>
              <p className="text-slate-600">Are you sure you want to continue?</p>
            </div>
          ) : (
            <div className="space-y-2">
              <p><strong>This will generate a new random webhook signing secret.</strong></p>
              <p className="text-slate-600">
                The generated secret is a locally-created <code className="rounded bg-slate-100 px-1">whsec_...</code> value.
                For <strong>production Stripe webhooks</strong>, you should use the signing secret from the Stripe Dashboard instead,
                as Stripe needs to know the secret to sign events.
              </p>
              <p className="text-slate-600">
                The generated secret is useful for testing or self-managed webhook endpoints.
                The new secret will be shown once — copy it immediately.
              </p>
              <p className="text-slate-600">Continue?</p>
            </div>
          )
        }
        confirmLabel={confirmAction?.action === 'rotate' ? 'Yes, Rotate Secret' : 'Generate Secret'}
        variant={confirmAction?.action === 'rotate' ? 'danger' : 'default'}
        loading={generateSecretMutation.isPending}
        onConfirm={() => {
          if (confirmAction) generateSecretMutation.mutate(confirmAction.key);
        }}
        onCancel={() => setConfirmAction(null)}
      />

      {/* Error toast */}
      {generateSecretMutation.isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          Failed to generate secret: {String(generateSecretMutation.error?.message ?? 'Unknown error')}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Auth-specific panel with education + auto-fill from rule42-auth
// ---------------------------------------------------------------------------

/**
 * Known rule42-auth configuration values for auto-fill.
 * These are the production values that the billing service should use to
 * connect to the rule42-auth OIDC provider.
 */
const RULE42_AUTH_DEFAULTS = {
  BILLING_AUTH__SERVICE_URL: 'https://auth.rule42.ai',
  BILLING_AUTH__CLIENT_ID: 'rule42-billing-service',
};

/**
 * Settings that are optional — shown with an "Optional" badge instead of "Set".
 */
const AUTH_OPTIONAL_KEYS = new Set<string>([
  'AUTH_WEBHOOK_ENDPOINT',
  'BILLING_SECRETS_VAULT_URL',
  'BILLING_SERVICE_TOKEN',
]);

function AuthCategoryPanel({ onClose }: { onClose: () => void }) {
  const { token, apiBaseUrl } = useAdminAuth();
  const queryClient = useQueryClient();
  const { data: allSettings } = useSettings();
  const settings = (allSettings ?? []).filter((s) => s.category === 'auth');
  const settingsMap = new Map(settings.map((s) => [s.key, s]));
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [showEducation, setShowEducation] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState<{ key: string; value: string } | null>(null);
  const [revealingKey, setRevealingKey] = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: async (updates: Record<string, string>) => {
      const res = await fetch(`${apiBaseUrl}/admin/settings`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings: Object.entries(updates).map(([key, value]) => ({ key, value })) }),
      });
      if (!res.ok) throw new Error(`Save failed: ${res.status}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-settings'] });
      setEdits({});
    },
  });

  const revealSecretMutation = useMutation({
    mutationFn: async (key: string) => {
      const res = await fetch(`${apiBaseUrl}/admin/settings/reveal/${key}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Reveal failed: ${res.status}`);
      return (await res.json()).data as { key: string; value: string };
    },
    onSuccess: (data) => {
      setRevealedSecret({ key: data.key, value: data.value });
      setRevealingKey(null);
    },
  });

  /** Auto-fill the known rule42-auth values (service URL + client ID). */
  function autoFillDefaults() {
    const newEdits: Record<string, string> = {};
    for (const [key, value] of Object.entries(RULE42_AUTH_DEFAULTS)) {
      const s = settingsMap.get(key);
      if (!s?.has_value) {
        newEdits[key] = value;
      }
    }
    if (Object.keys(newEdits).length > 0) {
      setEdits((prev) => ({ ...prev, ...newEdits }));
    }
  }

  function renderField(key: string) {
    const s = settingsMap.get(key);
    if (!s) return null;
    const isRevealed = revealedSecret?.key === key;
    const isOptional = AUTH_OPTIONAL_KEYS.has(key);

    return (
      <div key={key} className="space-y-1">
        <div className="flex items-center justify-between">
          <label className="block text-xs font-medium text-slate-600">{s.key}</label>
          <div className="flex items-center gap-2">
            {isOptional ? (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">Optional</span>
            ) : s.has_value ? (
              <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-700">Set</span>
            ) : (
              <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium text-red-700">Required</span>
            )}
            {s.is_secret && s.has_value && !isRevealed && (
              <button
                type="button"
                onClick={() => { setRevealingKey(key); revealSecretMutation.mutate(key); }}
                disabled={revealSecretMutation.isPending}
                className="text-xs text-slate-400 hover:text-slate-600"
                title="Reveal current value"
              >
                {revealSecretMutation.isPending && revealingKey === key ? '...' : '👁 Reveal'}
              </button>
            )}
          </div>
        </div>
        {s.description && <p className="text-xs text-slate-400">{s.description}</p>}
        {isRevealed ? (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={revealedSecret!.value}
                className="w-full rounded-lg border border-purple-300 bg-purple-50 px-3 py-2 font-mono text-sm text-purple-900"
              />
              <button
                type="button"
                onClick={() => navigator.clipboard?.writeText(revealedSecret!.value)}
                className="shrink-0 rounded-lg border border-slate-300 px-3 py-2 text-xs text-slate-600 hover:bg-slate-50"
              >
                Copy
              </button>
            </div>
            <button type="button" onClick={() => setRevealedSecret(null)} className="text-xs text-slate-400 hover:text-slate-600">
              Hide
            </button>
          </div>
        ) : (
          <input
            type={s.is_secret ? 'password' : 'text'}
            defaultValue={s.is_secret ? '' : s.value}
            placeholder={s.is_secret ? (s.has_value ? '•••••••• (leave blank to keep)' : 'Not set') : 'Not set'}
            value={edits[key] ?? ''}
            onChange={(e) => setEdits((prev) => ({ ...prev, [key]: e.target.value }))}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      {/* Auto-fill banner */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
        <div className="flex items-start justify-between gap-3">
          <p className="text-xs leading-relaxed text-blue-800">
            <strong>🔗 Rule42 Auth Integration</strong> — The billing service connects to Rule42 Auth for SSO,
            org membership lookup, and customer provisioning. Click "Auto-fill" to populate the known values
            from your Rule42 Auth deployment. You'll still need to set the client secret manually (see below).
          </p>
          <button
            type="button"
            onClick={autoFillDefaults}
            className="shrink-0 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
          >
            ⚡ Auto-fill
          </button>
        </div>
      </div>

      {/* Settings fields */}
      {settings.map((s) => renderField(s.key))}

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
          Cancel
        </button>
        <button
          onClick={() => { if (Object.keys(edits).length > 0) saveMutation.mutate(edits); }}
          disabled={Object.keys(edits).length === 0 || saveMutation.isPending}
          className="rounded-lg bg-purple-600 px-4 py-2 text-sm text-white hover:bg-purple-700 disabled:opacity-50"
        >
          {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* ─── Collapsible education section (bottom) ─── */}
      <div className="border-t border-slate-200 pt-4">
        <button
          type="button"
          onClick={() => setShowEducation(!showEducation)}
          className="flex w-full items-center justify-between text-sm font-medium text-slate-600 hover:text-slate-800"
        >
          <span>💡 Learn about Authentication settings</span>
          <span className="text-xs">{showEducation ? '▲ Hide' : '▼ Show'}</span>
        </button>
        {showEducation && (
          <div className="mt-3 space-y-4 text-xs leading-relaxed text-slate-600">
            <div>
              <strong>BILLING_AUTH__SERVICE_URL</strong>{' '}
              <span className="rounded-full bg-green-100 px-1.5 py-0.5 text-[9px] font-medium text-green-700">Required</span>
              <br />
              The base URL of your Rule42 Auth OIDC provider.
              For production: <code className="rounded bg-slate-100 px-1">https://auth.rule42.ai</code>.
              The billing server uses this to discover OIDC endpoints, validate JWTs, and call the auth API.
            </div>
            <div>
              <strong>BILLING_AUTH__CLIENT_ID</strong>{' '}
              <span className="rounded-full bg-green-100 px-1.5 py-0.5 text-[9px] font-medium text-green-700">Required</span>
              <br />
              The OIDC client ID for the billing service's service account.
              This is a confidential client (<code className="rounded bg-slate-100 px-1">client_credentials</code> grant) registered in Rule42 Auth.
              For production: <code className="rounded bg-slate-100 px-1">rule42-billing-service</code>.
              The billing server uses it to obtain access tokens for server-to-server API calls (e.g. syncing org membership, sending email via auth relay).
            </div>
            <div>
              <strong>BILLING_AUTH__CLIENT_SECRET</strong>{' '}
              <span className="rounded-full bg-green-100 px-1.5 py-0.5 text-[9px] font-medium text-green-700">Required</span>
              <br />
              The secret for the OIDC client above.
              <strong className="text-red-600"> This is a secret — keep it private.</strong>
              <br /><br />
              <strong>Where to get it:</strong> The client secret is set when the client is created in Rule42 Auth.
              If you don't have it, you can rotate it via the Rule42 Auth admin API:
              <code className="mt-1 block rounded bg-slate-100 p-2 text-[10px]">POST https://auth.rule42.ai/api/v1/admin/clients/rule42-billing-service/rotate-secret</code>
              This returns a new plaintext secret <strong>once</strong> — copy it immediately and paste it here.
            </div>
            <div>
              <strong>BILLING_AUTH__PROVIDER</strong>{' '}
              <span className="rounded-full bg-green-100 px-1.5 py-0.5 text-[9px] font-medium text-green-700">Required</span>
              <br />
              The auth provider type. For Rule42 Auth, use <code className="rounded bg-slate-100 px-1">rule42</code>.
              Other supported values: <code className="rounded bg-slate-100 px-1">auth0</code>, <code className="rounded bg-slate-100 px-1">clerk</code>, <code className="rounded bg-slate-100 px-1">custom</code>.
            </div>
            <div>
              <strong>AUTH_WEBHOOK_SECRET</strong>{' '}
              <span className="rounded-full bg-green-100 px-1.5 py-0.5 text-[9px] font-medium text-green-700">Required</span>
              <br />
              A shared HMAC secret used to verify that webhook events
              (e.g. user created, org member added) sent by Rule42 Auth to the billing service are genuine.
              Both services must have the same value. If you change it here, update it in the Rule42 Auth configuration too.
            </div>
            <div>
              <strong>AUTH_WEBHOOK_ENDPOINT</strong>{' '}
              <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-medium text-slate-500">Optional</span>
              <br />
              The path on the billing server where auth webhook events are received.
              Defaults to <code className="rounded bg-slate-100 px-1">/webhooks/auth</code> when not set.
              You only need to change this if your reverse proxy routes auth webhooks to a different path.
            </div>
            <div>
              <strong>BILLING_SECRETS_VAULT_URL</strong>{' '}
              <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-medium text-slate-500">Optional</span>
              <br />
              URL of the Rule42 Auth Secrets Vault endpoint.
              When set, the deploy script (<code className="rounded bg-slate-100 px-1">render-production-env-from-auth.mjs</code>)
              reads secrets from the vault at deploy time and injects them as environment variables.
              Without the vault, secrets are stored in the <code className="rounded bg-slate-100 px-1">platform_settings</code> database table
              (masked on read, but unencrypted at rest in Postgres).
              For production: <code className="rounded bg-slate-100 px-1">https://auth.rule42.ai/api/v1/secrets</code>.
            </div>
            <div>
              <strong>BILLING_SERVICE_TOKEN</strong>{' '}
              <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-medium text-slate-500">Optional</span>
              <br />
              An optional bearer token for service-to-service authentication bypass.
              When set, internal services (e.g. rule42-cloud) can call the billing API's usage and entitlements
              endpoints by presenting this token instead of a full JWT.
              <strong>Leave empty if you don't have another service that needs direct API access to billing.</strong>
              If you do need it, generate a random 64-hex-char token, set it here, and configure the same token
              on the calling service.
            </div>
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
              <strong>⚠️ Important:</strong> After saving auth settings, the billing server may need a restart for the
              changes to take effect (the config loader reads env vars at startup). Settings entered here are stored in
              the <code className="rounded bg-slate-100 px-1">platform_settings</code> database table and are read at
              runtime by the Stripe Connect routes and the email transport resolver.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Generic category panel (all non-Stripe categories)
// ---------------------------------------------------------------------------

function GenericCategoryPanel({ category, onClose }: { category: string; onClose: () => void }) {
  const { token, apiBaseUrl } = useAdminAuth();
  const queryClient = useQueryClient();
  const { data: allSettings } = useSettings();
  const settings = (allSettings ?? []).filter((s) => s.category === category);
  const [edits, setEdits] = useState<Record<string, string>>({});

  const saveMutation = useMutation({
    mutationFn: async (updates: Record<string, string>) => {
      const res = await fetch(`${apiBaseUrl}/admin/settings`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings: Object.entries(updates).map(([key, value]) => ({ key, value })) }),
      });
      if (!res.ok) throw new Error(`Save failed: ${res.status}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-settings'] });
      setEdits({});
    },
  });

  return (
    <div className="space-y-4 p-4">
      {settings.length === 0 && (
        <p className="text-sm text-slate-500">No settings found for this category.</p>
      )}
      {settings.map((s) => (
        <div key={s.key} className="space-y-1">
          <label className="block text-xs font-medium text-slate-600">{s.key}</label>
          {s.description && <p className="text-xs text-slate-400">{s.description}</p>}
          <input
            type={s.is_secret ? 'password' : 'text'}
            defaultValue={s.is_secret ? '' : s.value}
            placeholder={s.is_secret ? (s.has_value ? '........' : 'Not set') : 'Not set'}
            onChange={(e) => setEdits((prev) => ({ ...prev, [s.key]: e.target.value }))}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
          />
        </div>
      ))}
      <div className="flex justify-end gap-3 pt-4">
        <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
          Cancel
        </button>
        <button
          onClick={() => { if (Object.keys(edits).length > 0) saveMutation.mutate(edits); }}
          disabled={Object.keys(edits).length === 0 || saveMutation.isPending}
          className="rounded-lg bg-purple-600 px-4 py-2 text-sm text-white hover:bg-purple-700 disabled:opacity-50"
        >
          {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Platform Toggles Panel
// ---------------------------------------------------------------------------

function usePlatformToggles() {
  const { token, apiBaseUrl } = useAdminAuth();
  return useQuery<Record<string, string>>({
    queryKey: ['platform-toggles'],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/admin/platform-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return {};
      const data = await res.json();
      const settings: Record<string, string> = {};
      for (const item of data.data ?? []) {
        settings[item.key] = item.value;
      }
      return settings;
    },
  });
}

function useUpdateToggle() {
  const { token, apiBaseUrl } = useAdminAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { key: string; value: string }) => {
      const res = await fetch(`${apiBaseUrl}/admin/platform-settings/${params.key}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: params.value }),
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['platform-toggles'] }),
  });
}

interface ToggleDef {
  key: string;
  label: string;
  description: string;
  warning?: string;
}

const TOGGLES: ToggleDef[] = [
  {
    key: 'allow_transaction_deletion',
    label: 'Allow Transaction Deletion',
    description: 'When enabled, platform admins can delete credit transactions from the Credits Dashboard. Deleting a transaction reverses its effect on the balance. Disable this before going fully live.',
    warning: '⚠️ This bypasses the immutable audit trail. Only enable for pre-launch cleanup. Disable before production use.',
  },
];

function TogglesPanel({ onClose }: { onClose: () => void }): React.JSX.Element {
  const togglesQuery = usePlatformToggles();
  const updateMutation = useUpdateToggle();

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-slate-900">Platform Toggles</h3>
        <p className="mt-1 text-sm text-slate-500">Feature flags that control platform-level operations.</p>
      </div>

      {togglesQuery.isLoading && <p className="text-sm text-slate-400">Loading toggles...</p>}

      {togglesQuery.data && (
        <div className="space-y-4">
          {TOGGLES.map((toggle) => {
            const isEnabled = togglesQuery.data[toggle.key] === 'true';
            return (
              <div key={toggle.key} className="rounded-xl border border-slate-200 bg-white p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h4 className="font-medium text-slate-900">{toggle.label}</h4>
                    <p className="mt-1 text-sm text-slate-500">{toggle.description}</p>
                    {toggle.warning && (
                      <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                        <p className="text-xs text-amber-800">{toggle.warning}</p>
                      </div>
                    )}
                  </div>
                  <label className="flex flex-shrink-0 cursor-pointer items-center gap-2">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={isEnabled}
                      onClick={() => updateMutation.mutate({ key: toggle.key, value: isEnabled ? 'false' : 'true' })}
                      disabled={updateMutation.isPending}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                        isEnabled ? 'bg-purple-600' : 'bg-slate-300'
                      } disabled:opacity-50`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                          isEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <span className={`text-xs font-medium ${isEnabled ? 'text-purple-700' : 'text-slate-400'}`}>
                      {isEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex justify-end pt-4">
        <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
          Close
        </button>
      </div>
    </div>
  );
}
